import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  FileText,
  DollarSign,
  AlertTriangle,
  MoreHorizontal,
  Trash2,
  RefreshCw,
  Eye,
  TrendingUp,
  Calendar,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface Contract {
  id: string
  clientName: string
  name: string
  type: string
  status: string
  value: number | null
  currency: string
  billingCycle: string | null
  startDate: string
  endDate: string | null
  renewalDate: string | null
  autoRenew: boolean
  slaTargets: Record<string, unknown> | null
  notes: string | null
  createdAt: string
}

interface ContractStats {
  total: number
  active: number
  draft: number
  expiring: number
  expired: number
  cancelled: number
  totalValue: number
  totalMRR: number
  totalARR: number
  byType: Array<{ type: string; count: number; value: number }>
  expiringSoon: number
}

// ============================================
// Helpers
// ============================================

const contractTypeLabels: Record<string, string> = {
  managed_services: 'Managed Services',
  project: 'Project',
  retainer: 'Retainer',
  break_fix: 'Break/Fix',
  co_managed: 'Co-Managed',
}

function statusBadge(status: string) {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Active</Badge>
    case 'expiring':
      return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Expiring</Badge>
    case 'expired':
      return <Badge variant="destructive">Expired</Badge>
    case 'draft':
      return <Badge variant="secondary">Draft</Badge>
    case 'cancelled':
      return <Badge className="bg-gray-500/10 text-gray-500 border-gray-500/20">Cancelled</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function formatCurrency(value: number | null, currency: string = 'AUD'): string {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null
  const diff = new Date(dateStr).getTime() - Date.now()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

// ============================================
// Create Contract Dialog
// ============================================

function CreateContractDialog() {
  const [open, setOpen] = useState(false)
  const [clientName, setClientName] = useState('')
  const [name, setName] = useState('')
  const [type, setType] = useState('managed_services')
  const [status, setStatus] = useState('draft')
  const [value, setValue] = useState('')
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [notes, setNotes] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/contracts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      toast({ title: 'Contract created', description: 'New contract has been added' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create contract', variant: 'destructive' }),
  })

  function resetForm() {
    setClientName('')
    setName('')
    setType('managed_services')
    setStatus('draft')
    setValue('')
    setBillingCycle('monthly')
    setStartDate('')
    setEndDate('')
    setNotes('')
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> New Contract</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Contract</DialogTitle>
          <DialogDescription>Add a new contract or SLA agreement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client Name *</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Client Pty Ltd" />
            </div>
            <div className="space-y-2">
              <Label>Contract Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Managed IT Services" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="managed_services">Managed Services</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="retainer">Retainer</SelectItem>
                  <SelectItem value="break_fix">Break/Fix</SelectItem>
                  <SelectItem value="co_managed">Co-Managed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expiring">Expiring</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Value (AUD)</Label>
              <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="5000" min="0" />
            </div>
            <div className="space-y-2">
              <Label>Billing Cycle</Label>
              <Select value={billingCycle} onValueChange={setBillingCycle}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes..." rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              clientName,
              name,
              type,
              status,
              value: value ? parseFloat(value) : undefined,
              billingCycle,
              startDate,
              endDate: endDate || undefined,
              notes: notes || undefined,
            })}
            disabled={!clientName || !name || !startDate || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Contract'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Contract Detail Dialog
// ============================================

function ContractDetailDialog({ contract, onClose }: { contract: Contract; onClose: () => void }) {
  const [renewOpen, setRenewOpen] = useState(false)
  const [newEndDate, setNewEndDate] = useState('')
  const [newValue, setNewValue] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const renewMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.patch(`/contracts/${contract.id}/renew`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      toast({ title: 'Contract renewed', description: 'Contract has been renewed successfully' })
      setRenewOpen(false)
    },
    onError: () => toast({ title: 'Error', description: 'Failed to renew contract', variant: 'destructive' }),
  })

  const days = daysUntil(contract.endDate)

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{contract.name}</DialogTitle>
          <DialogDescription>{contract.clientName}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Type</p>
              <p className="text-sm font-medium">{contractTypeLabels[contract.type] || contract.type}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <div className="mt-0.5">{statusBadge(contract.status)}</div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Value</p>
              <p className="text-sm font-medium">{formatCurrency(contract.value, contract.currency)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Billing Cycle</p>
              <p className="text-sm font-medium capitalize">{contract.billingCycle || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Start Date</p>
              <p className="text-sm font-medium">{formatDate(contract.startDate)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">End Date</p>
              <p className="text-sm font-medium">{formatDate(contract.endDate)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Auto-Renew</p>
              <p className="text-sm font-medium">{contract.autoRenew ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Days Remaining</p>
              <p className={`text-sm font-medium ${days !== null && days <= 30 ? 'text-red-500' : days !== null && days <= 90 ? 'text-yellow-500' : 'text-green-500'}`}>
                {days !== null ? (days > 0 ? `${days} days` : 'Expired') : '-'}
              </p>
            </div>
          </div>
          {contract.notes && (
            <div>
              <p className="text-xs text-muted-foreground">Notes</p>
              <p className="text-sm mt-1">{contract.notes}</p>
            </div>
          )}

          {!renewOpen ? (
            <Button variant="outline" className="w-full" onClick={() => setRenewOpen(true)}>
              <RefreshCw className="mr-2 h-4 w-4" /> Renew Contract
            </Button>
          ) : (
            <div className="space-y-3 border rounded-lg p-4">
              <p className="text-sm font-medium">Renew Contract</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>New End Date *</Label>
                  <Input type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>New Value (optional)</Label>
                  <Input type="number" value={newValue} onChange={(e) => setNewValue(e.target.value)} min="0" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRenewOpen(false)}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={() => renewMutation.mutate({
                    newEndDate,
                    newValue: newValue ? parseFloat(newValue) : undefined,
                  })}
                  disabled={!newEndDate || renewMutation.isPending}
                >
                  {renewMutation.isPending ? 'Renewing...' : 'Confirm Renewal'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Calendar View
// ============================================

function CalendarView({ contracts }: { contracts: Contract[] }) {
  const monthContracts = useMemo(() => {
    const months: Record<string, Contract[]> = {}
    const now = new Date()

    for (let i = 0; i < 12; i++) {
      const month = new Date(now.getFullYear(), now.getMonth() + i, 1)
      const key = month.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
      months[key] = []
    }

    for (const contract of contracts) {
      if (!contract.endDate) continue
      const end = new Date(contract.endDate)
      const key = end.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
      if (months[key]) {
        months[key].push(contract)
      }
    }

    return months
  }, [contracts])

  return (
    <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-4">
      {Object.entries(monthContracts).map(([month, items]) => (
        <Card key={month} className={items.length > 0 ? 'border-yellow-500/30' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{month}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {items.length === 0 ? (
              <p className="text-xs text-muted-foreground">No renewals</p>
            ) : (
              <div className="space-y-1">
                {items.map((c) => (
                  <div key={c.id} className="text-xs">
                    <span className="font-medium">{c.clientName}</span>
                    <span className="text-muted-foreground"> - {c.name}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ============================================
// Main Page
// ============================================

export function ContractsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch contracts
  const { data: contractsData, isLoading } = useQuery({
    queryKey: ['contracts', statusFilter, typeFilter],
    queryFn: () => apiClient.get<{ data: Contract[]; meta: unknown }>('/contracts', {
      ...(statusFilter !== 'all' && { status: statusFilter }),
      ...(typeFilter !== 'all' && { type: typeFilter }),
      limit: 100,
    }),
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['contracts', 'stats'],
    queryFn: () => apiClient.get<ContractStats>('/contracts/stats'),
  })

  // Fetch expiring
  const { data: expiringData } = useQuery({
    queryKey: ['contracts', 'expiring'],
    queryFn: () => apiClient.get<Contract[]>('/contracts/expiring', { days: 90 }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/contracts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      toast({ title: 'Deleted', description: 'Contract has been cancelled' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete contract', variant: 'destructive' }),
  })

  const contracts = contractsData?.data?.data || contractsData?.data || []
  const stats = statsData?.data || null
  const expiringContracts = expiringData?.data || []

  const filtered = Array.isArray(contracts)
    ? contracts.filter((c: Contract) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.clientName.toLowerCase().includes(search.toLowerCase())
      )
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contracts & SLA</h1>
          <p className="text-muted-foreground">Manage client contracts, SLAs, and renewals</p>
        </div>
        <CreateContractDialog />
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Total Contracts</p>
            </div>
            <div className="text-2xl font-bold mt-1">{stats?.total ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">{stats?.active ?? 0} active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-500" />
              <p className="text-xs text-muted-foreground">Monthly Recurring</p>
            </div>
            <div className="text-2xl font-bold text-green-500 mt-1">
              {formatCurrency(stats?.totalMRR ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              ARR: {formatCurrency(stats?.totalARR ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <p className="text-xs text-muted-foreground">Expiring Soon</p>
            </div>
            <div className="text-2xl font-bold text-yellow-500 mt-1">{stats?.expiringSoon ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Within 90 days</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              <p className="text-xs text-muted-foreground">Total Value</p>
            </div>
            <div className="text-2xl font-bold text-blue-500 mt-1">
              {formatCurrency(stats?.totalValue ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Active contracts</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="table">
        <TabsList>
          <TabsTrigger value="table">Table View</TabsTrigger>
          <TabsTrigger value="calendar">Renewal Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search contracts..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expiring">Expiring</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[170px]"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="managed_services">Managed Services</SelectItem>
                <SelectItem value="project">Project</SelectItem>
                <SelectItem value="retainer">Retainer</SelectItem>
                <SelectItem value="break_fix">Break/Fix</SelectItem>
                <SelectItem value="co_managed">Co-Managed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {isLoading ? (
            <Card>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex gap-4">
                      <Skeleton className="h-5 w-40" />
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-5 w-20" />
                      <Skeleton className="h-5 w-24" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">No contracts found</p>
                <p className="text-sm text-muted-foreground">Create your first contract to get started</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Contract</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Billing</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((contract: Contract) => {
                    const days = daysUntil(contract.endDate)
                    return (
                      <TableRow key={contract.id} className="cursor-pointer" onClick={() => setSelectedContract(contract)}>
                        <TableCell className="font-medium">{contract.clientName}</TableCell>
                        <TableCell>{contract.name}</TableCell>
                        <TableCell>
                          <span className="text-sm">{contractTypeLabels[contract.type] || contract.type}</span>
                        </TableCell>
                        <TableCell>{statusBadge(contract.status)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(contract.value, contract.currency)}
                        </TableCell>
                        <TableCell className="capitalize text-sm">{contract.billingCycle || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{formatDate(contract.endDate)}</span>
                            {days !== null && days <= 90 && days > 0 && (
                              <span className={`text-xs ${days <= 30 ? 'text-red-500' : 'text-yellow-500'}`}>
                                ({days}d)
                              </span>
                            )}
                            {days !== null && days <= 0 && (
                              <span className="text-xs text-red-500">(expired)</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedContract(contract); }}>
                                <Eye className="mr-2 h-4 w-4" /> View Details
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(contract.id); }}>
                                <Trash2 className="mr-2 h-4 w-4" /> Cancel Contract
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Contract renewals and expirations over the next 12 months
              </p>
            </div>

            {/* Expiring Soon Alert */}
            {Array.isArray(expiringContracts) && expiringContracts.length > 0 && (
              <Card className="border-yellow-500/30 bg-yellow-500/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    Contracts Expiring Within 90 Days
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {expiringContracts.map((c: Contract) => (
                      <div key={c.id} className="flex items-center justify-between text-sm">
                        <span>
                          <span className="font-medium">{c.clientName}</span>
                          <span className="text-muted-foreground"> - {c.name}</span>
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{formatDate(c.endDate)}</span>
                          {statusBadge(c.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <CalendarView contracts={Array.isArray(contracts) ? contracts : []} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Contract Detail Dialog */}
      {selectedContract && (
        <ContractDetailDialog
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
        />
      )}
    </div>
  )
}
